import { View, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native'
import { Link, router } from 'expo-router'
import type { PropsWithChildren } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { Section } from '@/src/common/components/Section'
import { useMinWidth1024 } from '@/src/common/wrappers/MainWrapper'
import { maxWidth } from '@/src/common/wrappers/MainWrapper'
import { classes, mesaColorNames, spacing, typography } from '@/src/common/theme'
import { NotificationComponents } from '@/src/experiences/notifications/components'
import { NotificationsRoutes } from '@/src/experiences/notifications/navigation/routes'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useMenuIsFullWidth } from '@/src/app/main/menu'
import { useMutation } from '@apollo/client'
import { Mutations, Queries } from '../gql'
import { UserPushNotificationStatus } from '@api/mesa/graphql'
import { GET_UNREAD_NOTIFICATIONS } from '@/src/common/components/Profile/NotificationsEntryPoint'

const minVerticalMargin = 48

export const notificationCenterHorizontalPadding = spacing['4']
export const notificationCenterVerticalPadding = 10

export function NotificationCenterScrollView(props: PropsWithChildren) {
  const menuIsFullWidth = useMenuIsFullWidth()
  const safeAreaInsets = useSafeAreaInsets()
  const minWidth1024 = useMinWidth1024()
  const margin = menuIsFullWidth ? 16 : minWidth1024 ? 40 : 24
  const { width: windowWidth } = useWindowDimensions()

  return (
    <>
      <View style={{ width: 0, flexBasis: '17%', flexShrink: 1, minHeight: minVerticalMargin }} />
      <View
        style={[
          {
            position: menuIsFullWidth ? 'absolute' : 'relative',
            backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
            marginTop: menuIsFullWidth ? margin : 0,
            marginVertical: margin,
            marginHorizontal:
              windowWidth > maxWidth ? (windowWidth - maxWidth) / 2 + margin : margin,
            paddingBottom: 0,
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
            borderRadius: 16,
            overflow: 'hidden',
          },
          menuIsFullWidth
            ? safeAreaInsets
            : {
                width: '100%',
                maxWidth: 484,
                marginTop: 0,
                marginHorizontal: 'auto',
                marginVertical: 0,
                flexGrow: 0,
                flexShrink: 1,
                flexBasis: 'auto',
              },
          classes.cardShadow,
        ]}
      >
        {props.children}
      </View>
      <View style={{ width: 0, flexBasis: '17%', flexGrow: 1, minHeight: minVerticalMargin }} />
    </>
  )
}

function Center() {
  const { height: windowHeight } = useWindowDimensions()

  const [updateAllNotificationsPushStatusTo] = useMutation(
    Mutations.UPDATE_ALL_NOTIFICATIONS_PUSH_STATUS_TO,
  )

  return (
    <Animated.View
      entering={FadeIn}
      exiting={FadeOut}
      style={[StyleSheet.absoluteFill, { width: '100%', height: '100%' }]}
    >
      <TouchableOpacity
        activeOpacity={1}
        onPress={() => router.dismiss()}
        style={[StyleSheet.absoluteFill, { backgroundColor: '#00000050' }]}
      >
        <View style={{ height: windowHeight * 2 }} />
      </TouchableOpacity>
      <NotificationCenterScrollView>
        <Section
          styleOverride={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            margin: 0,
            height: 'auto',
            paddingBottom: notificationCenterVerticalPadding,
            paddingTop: 24,
            paddingLeft: 24,
            paddingRight: 24,
            backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
          }}
          title="Notification Center"
          titleStyles={[
            {
              color: mesaColorNames['Mesa - Brand/Secondary #223843'],
              marginLeft: 0,
            },
            typography['label/lg-bold'],
          ]}
          closeStyle={{ position: 'relative', right: 0 }}
          onClosePress={() => router.dismiss()}
          right={(close) => (
            <>
              <TouchableOpacity
                testID="update-all-notification-push-status-to-opened"
                style={{ marginLeft: 'auto' }}
                onPress={() =>
                  void updateAllNotificationsPushStatusTo({
                    variables: { pushNotificationStatus: UserPushNotificationStatus.Opened },
                    refetchQueries: [Queries.GET_USER_NOTIFICATIONS, GET_UNREAD_NOTIFICATIONS],
                  })
                }
              >
                <MaterialCommunityIcons
                  name="check-all"
                  size={24}
                  style={{ marginRight: 12 }}
                  color={mesaColorNames['Mesa - Brand/Secondary #223843']}
                />
              </TouchableOpacity>

              <Link href={NotificationsRoutes.Settings.route} dismissTo asChild>
                <TouchableOpacity testID="notification-settings">
                  <Ionicons
                    name="settings-outline"
                    size={24}
                    style={{ marginRight: 12 }}
                    color={mesaColorNames['Mesa - Brand/Secondary #223843']}
                  />
                </TouchableOpacity>
              </Link>
              {close}
            </>
          )}
        />
        <NotificationComponents.Feed />
      </NotificationCenterScrollView>
    </Animated.View>
  )
}

export const CenterScreen = {
  name: 'notifications-center',
  Screen: Center,
} as const
